import React from "react";
import { FiCheck } from "react-icons/fi";
import { RecipientContact } from "../../pages/compose/ChooseRecipientsPage";

// Type-safe icon wrapper
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;

// Type badge colors
const TYPE_COLORS: Record<string, string> = {
  INVESTOR: "#8b5cf6", // Purple
  ADVISOR: "#d4a574", // Gold
  CUSTOMER: "#22c55e", // Green
  TEAM: "#3b82f6", // Blue
  PARTNER: "#ec4899", // Pink
  OTHER: "#6b7280", // Gray
};

interface RecipientsTableProps {
  contacts: RecipientContact[];
  selectedIds: Set<string>;
  onToggleContact: (id: string) => void;
  onToggleAll: () => void;
}

/**
 * RecipientsTable - Table displaying contacts with selection checkboxes
 */
const RecipientsTable: React.FC<RecipientsTableProps> = ({
  contacts,
  selectedIds,
  onToggleContact,
  onToggleAll,
}) => {
  const allSelected = contacts.length > 0 && contacts.every((c) => selectedIds.has(c.id));
  const someSelected = contacts.some((c) => selectedIds.has(c.id)) && !allSelected;

  const getTypeColor = (type: string) => {
    return TYPE_COLORS[type] || TYPE_COLORS.OTHER;
  };

  if (contacts.length === 0) {
    return (
      <div className="su-recipients-empty">
        <span className="su-recipients-empty-text">
          No contacts found in this category
        </span>
      </div>
    );
  }

  return (
    <div className="su-recipients-table-wrapper">
      <table className="su-recipients-table">
        <thead>
          <tr className="su-recipients-table-header">
            <th className="su-recipients-th su-recipients-th-checkbox">
              <button
                className={`su-recipients-checkbox ${
                  allSelected ? "checked" : someSelected ? "indeterminate" : ""
                }`}
                onClick={onToggleAll}
                aria-label="Select all"
              >
                {(allSelected || someSelected) && (
                  <CheckIcon className="w-3 h-3" />
                )}
              </button>
            </th>
            <th className="su-recipients-th">Name</th>
            <th className="su-recipients-th">Email</th>
            <th className="su-recipients-th">Type</th>
          </tr>
        </thead>
        <tbody>
          {contacts.map((contact) => {
            const isSelected = selectedIds.has(contact.id);
            const type = contact.type || "OTHER";
            const typeColor = getTypeColor(type);

            return (
              <tr
                key={contact.id}
                className={`su-recipients-row ${isSelected ? "selected" : ""}`}
                onClick={() => onToggleContact(contact.id)}
              >
                <td className="su-recipients-td su-recipients-td-checkbox">
                  <button
                    className={`su-recipients-checkbox ${isSelected ? "checked" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleContact(contact.id);
                    }}
                    aria-label={`Select ${contact.name || contact.email}`}
                  >
                    {isSelected && <CheckIcon className="w-3 h-3" />}
                  </button>
                </td>
                <td className="su-recipients-td su-recipients-td-name">
                  {contact.name || "No Name"}
                </td>
                <td className="su-recipients-td su-recipients-td-email">
                  {contact.email}
                </td>
                <td className="su-recipients-td su-recipients-td-type">
                  <span
                    className="su-recipients-type-badge"
                    style={{ backgroundColor: typeColor }}
                  >
                    {type}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default RecipientsTable;


